/**
 * Écran de configuration de l'export « Conseil municipal ».
 * Accessible via le bouton « Export conseil » de la vue maire consolidée.
 *
 * Le maire choisit : la période (avec raccourcis), le mode de comparaison
 * (période précédente / année précédente), les services à inclure, puis
 * télécharge en PDF ou en Excel.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText, FileSpreadsheet, Calendar } from 'lucide-react';
import { api } from '@/lib/api';
import { useModules } from '@/lib/modules-context';

// Modules de l'export : clé backend + libellé + clé service pour le filtrage actif.
const MODULES: { key: string; label: string; service?: string }[] = [
  { key: 'service_technique', label: 'Service technique' },
  { key: 'grc', label: 'Relation citoyen' },
  { key: 'scolaire_cantine', label: 'Scolaire et cantine', service: 'scolaire_cantine' },
  { key: 'associations', label: 'Vie associative', service: 'associations' },
  { key: 'salles', label: 'Salles et matériel' },
  { key: 'rh', label: 'Ressources humaines' },
  { key: 'comptabilite', label: 'Comptabilité', service: 'comptable' },
  { key: 'petite_enfance', label: 'Petite enfance', service: 'petite_enfance' },
  { key: 'urbanisme', label: 'Urbanisme', service: 'urbanisme' },
];

function isoToday(): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Paris', year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(new Date());
}
function premierDuMois(): string {
  const t = isoToday();
  return `${t.slice(0, 8)}01`;
}

export default function ExportConseilPage() {
  const navigate = useNavigate();
  const { isModuleActif } = useModules();

  const modulesDisponibles = MODULES.filter((m) => isModuleActif(m.service));

  const [debut, setDebut] = useState(premierDuMois());
  const [fin, setFin] = useState(isoToday());
  const [comparaison, setComparaison] = useState<'periode_precedente' | 'annee_precedente'>('annee_precedente');
  const [coches, setCoches] = useState<Set<string>>(new Set(modulesDisponibles.map((m) => m.key)));
  const [busy, setBusy] = useState<null | 'pdf' | 'xlsx'>(null);
  const [error, setError] = useState<string | null>(null);

  function toggle(key: string) {
    setCoches((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }

  function appliquerRaccourci(type: 'mois' | 'trimestre' | 'annee') {
    const t = isoToday();
    const [y, m] = t.split('-').map(Number);
    if (type === 'mois') {
      setDebut(`${t.slice(0, 8)}01`);
      setFin(t);
    } else if (type === 'trimestre') {
      const moisDebut = Math.floor(((m as number) - 1) / 3) * 3 + 1;
      setDebut(`${y}-${String(moisDebut).padStart(2, '0')}-01`);
      setFin(t);
    } else {
      setDebut(`${y}-01-01`);
      setFin(t);
    }
  }

  async function telecharger(format: 'pdf' | 'xlsx') {
    setError(null);
    if (fin < debut) { setError('La date de fin doit être postérieure à la date de début.'); return; }
    if (coches.size === 0) { setError('Sélectionnez au moins un service.'); return; }
    setBusy(format);
    try {
      await api.dashboard.exportConseilDownload(format, {
        debut, fin, modules: Array.from(coches), comparaison,
      });
    } catch {
      setError('Le téléchargement a échoué. Réessayez.');
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="mx-auto max-w-3xl px-4 py-6">
      <div className="mb-1 flex items-center gap-3">
        <button
          type="button"
          onClick={() => navigate('/dashboard/maire')}
          aria-label="Retour"
          className="flex h-9 w-9 items-center justify-center rounded-r-md border border-border bg-surface/60 text-ink-muted transition-colors hover:bg-surface"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        </button>
        <div>
          <div className="text-xs uppercase tracking-wide text-ink-subtle">Vue maire · Conseil municipal</div>
          <h1 className="font-display text-lg font-semibold text-ink">Export pour le conseil</h1>
        </div>
      </div>
      <p className="mb-5 mt-2 text-sm text-ink-muted">
        Génère un rapport synthétique des services de la commune, à présenter en séance. Choisis la période, les services à inclure, puis le format.
      </p>

      {error && (
        <div className="mb-4 rounded-r-md border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger">{error}</div>
      )}

      {/* Période */}
      <div className="mb-3 rounded-r-xl border border-border bg-surface/40 p-4">
        <div className="mb-3 flex items-center gap-2 text-sm font-medium text-ink">
          <Calendar className="h-4 w-4 text-ink-muted" aria-hidden="true" /> Période
        </div>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="mb-1 block text-xs text-ink-muted">Du</span>
            <input type="date" value={debut} max={fin} onChange={(e) => setDebut(e.target.value)}
              className="w-full rounded-r-md border border-border bg-surface px-3 py-2 text-sm text-ink" />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs text-ink-muted">Au</span>
            <input type="date" value={fin} min={debut} onChange={(e) => setFin(e.target.value)}
              className="w-full rounded-r-md border border-border bg-surface px-3 py-2 text-sm text-ink" />
          </label>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          <button type="button" onClick={() => appliquerRaccourci('mois')} className="rounded-r-md border border-border px-2.5 py-1 text-xs text-ink-muted hover:bg-surface">Mois en cours</button>
          <button type="button" onClick={() => appliquerRaccourci('trimestre')} className="rounded-r-md border border-border px-2.5 py-1 text-xs text-ink-muted hover:bg-surface">Trimestre</button>
          <button type="button" onClick={() => appliquerRaccourci('annee')} className="rounded-r-md border border-border px-2.5 py-1 text-xs text-ink-muted hover:bg-surface">Année</button>
        </div>
      </div>

      {/* Comparaison */}
      <div className="mb-3 rounded-r-xl border border-border bg-surface/40 p-4">
        <div className="mb-3 text-sm font-medium text-ink">Comparer à</div>
        <div className="grid grid-cols-2 gap-3">
          <button type="button" onClick={() => setComparaison('annee_precedente')}
            className={`rounded-r-md border px-3 py-2 text-left text-sm transition-colors ${comparaison === 'annee_precedente' ? 'border-tech bg-tech/10 text-ink' : 'border-border text-ink-muted hover:bg-surface'}`}>
            <span className="block font-medium">Année précédente</span>
            <span className="text-xs text-ink-subtle">même période, un an plus tôt</span>
          </button>
          <button type="button" onClick={() => setComparaison('periode_precedente')}
            className={`rounded-r-md border px-3 py-2 text-left text-sm transition-colors ${comparaison === 'periode_precedente' ? 'border-tech bg-tech/10 text-ink' : 'border-border text-ink-muted hover:bg-surface'}`}>
            <span className="block font-medium">Période précédente</span>
            <span className="text-xs text-ink-subtle">la durée juste avant</span>
          </button>
        </div>
      </div>

      {/* Services */}
      <div className="mb-3 rounded-r-xl border border-border bg-surface/40 p-4">
        <div className="mb-3 flex items-center justify-between">
          <div className="text-sm font-medium text-ink">Services à inclure</div>
          <button type="button"
            onClick={() => setCoches(coches.size === modulesDisponibles.length ? new Set() : new Set(modulesDisponibles.map((m) => m.key)))}
            className="rounded-r-md border border-border px-2.5 py-1 text-xs text-ink-muted hover:bg-surface">
            {coches.size === modulesDisponibles.length ? 'Tout décocher' : 'Tout cocher'}
          </button>
        </div>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {modulesDisponibles.map((m) => (
            <label key={m.key} className="flex cursor-pointer items-center gap-2 rounded-r-md border border-border px-3 py-2 text-sm text-ink hover:bg-surface">
              <input type="checkbox" checked={coches.has(m.key)} onChange={() => toggle(m.key)} />
              {m.label}
            </label>
          ))}
        </div>
      </div>

      {/* Format / téléchargement */}
      <div className="rounded-r-xl border border-border bg-surface/40 p-4">
        <div className="mb-3 text-sm font-medium text-ink">Format</div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <button type="button" disabled={busy !== null} onClick={() => void telecharger('pdf')}
            className="flex items-center justify-center gap-2 rounded-r-md border-2 border-tech bg-tech/10 px-4 py-3 text-sm font-medium text-tech-fg transition-colors hover:bg-tech/20 disabled:opacity-60">
            <FileText className="h-4 w-4" aria-hidden="true" />
            {busy === 'pdf' ? 'Génération…' : 'Télécharger en PDF'}
          </button>
          <button type="button" disabled={busy !== null} onClick={() => void telecharger('xlsx')}
            className="flex items-center justify-center gap-2 rounded-r-md border border-border px-4 py-3 text-sm font-medium text-ink transition-colors hover:bg-surface disabled:opacity-60">
            <FileSpreadsheet className="h-4 w-4" aria-hidden="true" />
            {busy === 'xlsx' ? 'Génération…' : 'Télécharger en Excel'}
          </button>
        </div>
      </div>
    </div>
  );
}
